/* eslint-disable @typescript-eslint/no-explicit-any */
import { NextPage } from 'next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { ReactElement, ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { preparePageLoadAnalytics } from '../lib/utils/analytics';
import AlertProvider from '../components/alert/alertProvider';
import {
  checkReturnUrl,
  getAdrumConfigSpace,
  getCurrentEnv,
  isCaremark,
  isSpecialty
} from '../lib/utils/utils';
import '../register-web-components';
import '../styles/global.scss';
import '@digital-pulse/react/style.css';
import '@digital-pulse/design-tokens/blue.css';
import '@pulse-community/typography-css/dist/main.css';
import { Provider } from '../context';
import { FeatureFlagWrapper } from '../components/featureFlagWrapper';
import Script from 'next/script';
import { I18nContextObj, I18nDefaultProvider } from '@cvsdigital_web/react-cmk-i18n';
import {
  getPreferredLanguage,
  getTranslateApiKey,
  getTranslateApiUrl
} from '../lib/config/translationConfig';

// eslint-disable-next-line @typescript-eslint/no-require-imports
global.XMLHttpRequest = require('xhr2');

type NextPageWithLayout = NextPage & {
  getLayout?: (pageProps: AppProps, page: ReactElement) => ReactNode;
};
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const utagFolder = isSpecialty() ? 'cvscaremarkspecialtyrx' : isCaremark() ? 'fast' : 'cvs';
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false
    }
  }
});
const currentEnv = getCurrentEnv();

function MyApp({ Component, pageProps }: AppPropsWithLayout | any) {
  if (typeof window !== 'undefined') {
    const cancelURL = sessionStorage.getItem('cancelReturnUrl');
    if (!cancelURL) {
      let ref = document?.referrer;
      //TODO Remove this once we turn on the flag
      if (ref?.includes('/pharmacy/v1') && !ref?.includes('h4l_caregiving'))
        ref = '/pharmacy/v1/#/pharmacy-settings/cghome';
      checkReturnUrl(ref);
      sessionStorage?.setItem('cancelReturnUrl', ref);
    }
    preparePageLoadAnalytics();
  }

  const env = currentEnv === '' || currentEnv === 'PROD' ? 'prod' : 'qa';

  const i18nContext = new I18nContextObj(
    undefined, // storage (uses default)
    getTranslateApiKey() as any, // apiKey - environment-based
    getTranslateApiUrl() as any, // apiUrl - LOB-based
    undefined, // debounce (uses default)
    true // apiEnabled - available for all LOBs
  );

  // Set preferred language if available
  if (typeof window !== 'undefined') {
    const preferredLanguage = getPreferredLanguage();
    if (preferredLanguage) {
      i18nContext.i18n.setLanguage(preferredLanguage);
    }
  }

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1"></meta>
        {typeof window !== 'undefined' && (
          <>
            <script
              type={'text/javascript'}
              src={`https://tags.tiqcdn.com/utag/cvs/${utagFolder}/${env}/utag.sync.js`}
            />
            <script
              id={'id-utag'}
              type={'text/javascript'}
              async={true}
              src={`https://tags.tiqcdn.com/utag/cvs/${utagFolder}/${env}/utag.js`}
            />
            <Script id={'id-adrum-config'}>
              (function(){(window['adrum-config-space'] = getAdrumConfigSpace(env))})();
            </Script>
          </>
        )}
      </Head>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <I18nDefaultProvider value={i18nContext}>
          <Provider>
            <AlertProvider>
              <Component {...pageProps} />
            </AlertProvider>
          </Provider>
        </I18nDefaultProvider>
      </QueryClientProvider>
    </>
  );
}

export default FeatureFlagWrapper(MyApp);
